import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import type { Chart as HighchartsChart, SVGRenderer, SVGElement, SeriesLineOptions } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { CHART_COLORS } from "app/constants/ChartColors";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { AssetsService } from "app/services/AssetsService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import { chartOptions, DemandAnalysisLineChart } from "../../components/DemandAnalysisLineChart";

import { SearchVolumeOverTimeChartTooltip } from "./SearchVolumeOverTimeChartTooltip";
import type { ILineChartSeriesData } from "./searchVolumeOverTimeSelectors";
import type { IChartTooltipDataItem } from "./types";
import { useDemandTrendsSpikeAnalysis } from "./useDemandTrendsSpikeAnalysis";

const { currentPeriod: currentPeriodColor, previousPeriod: previousPeriodColor } = CHART_COLORS.periodOverPeriod.Common;
const anomalyColor = colorsPalettes.blue["400"];

const IconButtonStyled = styled(IconButton)`
    padding: 10px;
    width: auto !important;
    border-radius: 18px;
    background: radial-gradient(
        167.71% 94.57% at 96.11% 3.99%,
        rgba(2, 199, 158, 0.3) 0%,
        rgba(3, 194, 162, 0.3) 17%,
        rgba(6, 180, 175, 0.3) 37%,
        rgba(11, 156, 196, 0.3) 59%,
        rgba(18, 123, 225, 0.3) 81%,
        rgba(25, 90, 254, 0.3) 100%
    );

    .inner-label {
        color: ${colorsPalettes.carbon["500"]};
    }
    ${SWReactIcons} {
        svg {
            path {
                fill: ${colorsPalettes.carbon["500"]} !important;
            }
        }
    }
`;

const PulseStyles = styled.div`
    .pulse-animation {
        animation: pulse-grow 2s ease-out infinite;
    }

    @keyframes pulse-grow {
        0% {
            transform: scale(0);
            opacity: 1;
        }
        100% {
            transform: scale(1);
            opacity: 0;
        }
    }
`;

function definePulseGradient(renderer: SVGRenderer, hexColor: string) {
    const id = "pulse-gradient";
    if (renderer.defs.element.querySelector(`#${id}`)) {
        return;
    }

    const gradient = document.createElementNS("http://www.w3.org/2000/svg", "radialGradient");
    gradient.setAttribute("id", id);
    gradient.setAttribute("cx", "50%");
    gradient.setAttribute("cy", "50%");
    gradient.setAttribute("r", "50%");

    const stops = [
        { offset: "0", color: rgba(hexColor, 0.7) },
        { offset: "0.4", color: rgba(hexColor, 0.4) },
        { offset: "1", color: rgba(hexColor, 0.0) },
    ];

    stops.forEach(({ offset, color }) => {
        const stop = document.createElementNS("http://www.w3.org/2000/svg", "stop");
        stop.setAttribute("offset", offset);
        stop.setAttribute("stop-color", color);
        gradient.appendChild(stop);
    });

    renderer.defs.element.appendChild(gradient);
}

/**
 * Creates an animated pulse effect at the specified coordinates using Highcharts renderer.
 */
function animatePulse(renderer: SVGRenderer, x: number, y: number): SVGElement {
    const pulse = renderer
        .circle(x, y, 60)
        .attr({ fill: "url(#pulse-gradient)", zIndex: 0, class: "pulse-animation" })
        .add();

    if (pulse.element) {
        pulse.element.style.transformOrigin = `${x}px ${y}px`;
    }

    return pulse;
}

interface ISearchVolumeOverTimeChartProps {
    anomalyDates: string[];
    chartData: ILineChartSeriesData[];
    categories: string[];
    chartTooltipData: IChartTooltipDataItem[];
}

export const SearchVolumeOverTimeChart: FC<ISearchVolumeOverTimeChartProps> = ({
    anomalyDates,
    chartData,
    categories,
    chartTooltipData,
}) => {
    const { params } = useCurrentStateAndParams();
    const { analyzeSpike } = useDemandTrendsSpikeAnalysis();
    const { isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const periodOverPeriodType = DurationService.getPeriodOverPeriodCompareType();

    // Usually we store chart reference in a ref, which is handy because we are not causing a re-render
    // But in this case we need to trigger the effect so that the logic depending on chart and anomaly dates works as expected
    const [chart, setChart] = useState<HighchartsChart | null>(null);

    const updatedPoints = useRef(new WeakSet());
    const pulseElements = useRef<Set<SVGElement>>(new Set());

    useEffect(() => {
        if (anomalyDates.length > 0) {
            allTrackers.dispatchTrackEvent(
                "demandTrendsGraphSpikeImpression",
                "impression",
                "demand.trends.graph.spike.impression",
                { ...params },
            );
        }
    }, [anomalyDates, params]);

    useEffect(() => {
        if (chart && anomalyDates.length > 0) {
            chart.series.forEach((series) => {
                series.points.forEach((point) => {
                    const pointCategory = typeof point.category === "string" ? point.category : String(point.category);
                    if (anomalyDates.includes(pointCategory) && !updatedPoints.current.has(point)) {
                        point.update(
                            {
                                marker: {
                                    // @ts-expect-error hasAnomaly does not exist in type PointMarkerOptionsObject
                                    hasAnomaly: true,
                                    enabled: true,
                                    symbol: `url(${AssetsService.assetUrl("/images/trend-analyzer/spike-indicator-icon.svg")})`,
                                    states: {
                                        hover: {
                                            enabled: true,
                                            radiusPlus: 2,
                                        },
                                    },
                                },
                            },
                            false, // Disable immediate update because we will redraw the whole chart at once later
                        );

                        updatedPoints.current.add(point);
                    }
                });
            });

            chart.redraw();
        }
    }, [chartData, anomalyDates, chart]);

    const options = useMemo(() => {
        return chartOptions({
            chart: {
                height: 250,
                events: {
                    load() {
                        setChart(this);
                        definePulseGradient(this.renderer, anomalyColor);
                    },
                    redraw() {
                        pulseElements.current.forEach((pulse) => {
                            if (pulse && pulse.destroy) {
                                pulse.destroy();
                            }
                        });
                        pulseElements.current.clear();

                        this.series.forEach((series) => {
                            series.points.forEach((point) => {
                                // @ts-expect-error hasAnomaly does not exist in type PointMarkerOptionsObject
                                const hasAnomaly = point.marker?.hasAnomaly;
                                if (hasAnomaly && typeof point.plotX === "number" && typeof point.plotY === "number") {
                                    const pulseElement = animatePulse(
                                        this.renderer,
                                        point.plotX + this.plotLeft,
                                        point.plotY + this.plotTop,
                                    );
                                    pulseElements.current.add(pulseElement);
                                }
                            });
                        });
                    },
                },
            },
            xAxis: {
                categories,
                labels: {
                    formatter() {
                        return dayjs
                            .utc(this.value as string)
                            .format(isWindow ? toggleItems.Daily.format : toggleItems.Monthly.format);
                    },
                },
            },
            tooltip: {
                stickOnContact: true,
                formatter() {
                    const index = this.x;
                    const tooltipDataItem = chartTooltipData[index];
                    const date = this.options?.date;

                    if (!tooltipDataItem) {
                        return null;
                    }

                    // @ts-expect-error hasAnomaly does not exist in type PointMarkerOptionsObject
                    const hasAnomaly = this.point?.marker?.hasAnomaly as boolean;

                    // Prepare anomaly point data if needed
                    let anomalyDate: string | undefined;
                    if (hasAnomaly) {
                        anomalyDate = date;
                    }

                    if (periodOverPeriodType != null) {
                        tooltipDataItem.name = translate("common.total");
                    } else {
                        tooltipDataItem.rows = tooltipDataItem.rows.map((row) => ({
                            ...row,
                            label: {
                                ...row.label,
                                name: translate("common.total"),
                            },
                        }));
                    }

                    return renderToStaticMarkup(
                        <SearchVolumeOverTimeChartTooltip
                            tooltipDataItem={tooltipDataItem}
                            periodOverPeriodType={periodOverPeriodType}
                            isDaily={!!isWindow}
                            untilDate={swSettings.current.windowEndDate}
                            anomalyDate={anomalyDate}
                            duration={params.duration}
                        />,
                    );
                },
            },
        });
    }, [categories, chartTooltipData, periodOverPeriodType, isWindow, setChart]);

    const series = useMemo(() => {
        return chartData.map((chartDataItem) => {
            return {
                id: chartDataItem.id,
                name: chartDataItem.name,
                color: chartDataItem.isPreviousPeriod ? previousPeriodColor : currentPeriodColor,
                type: "line",
                data: chartDataItem.data,
                dashStyle: "Solid",
                zoneAxis: "x",
                zones: chartDataItem.monthToDateIndex
                    ? [{ value: chartDataItem.monthToDateIndex - 1 }, { dashStyle: "Dot" }]
                    : undefined,
                marker: {
                    enabled: chartDataItem.data.length === 1,
                    fillColor: chartDataItem.isPreviousPeriod ? previousPeriodColor : currentPeriodColor,
                },
            } satisfies SeriesLineOptions;
        });
    }, [chartData]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (e.target instanceof Element) {
                const button = e.target.closest(".anomaly-button");
                if (button) {
                    const anomalyDate = button.getAttribute("id");
                    if (anomalyDate && anomalyDates.includes(anomalyDate)) {
                        try {
                            analyzeSpike(anomalyDate);
                        } catch (err) {
                            console.error("Failed to parse point data:", err);
                        }
                    }
                }
            }
        };

        document.addEventListener("click", handler);
        return () => {
            document.removeEventListener("click", handler);
        };
    }, [anomalyDates, analyzeSpike]);

    useEffect(() => {
        const pulses = pulseElements.current;
        return () => {
            pulses.forEach((pulse) => {
                if (pulse && pulse.destroy) {
                    pulse.destroy();
                }
            });
            pulses.clear();
        };
    }, []);

    return (
        <PulseStyles>
            <div data-automation="DemandOverTime-Total-Widget">
                {chartData.length > 0 ? (
                    <Flex
                        flexDirection="column"
                        flexGrow={1}
                        height="100%"
                        gap="30px"
                        justifyContent="space-between"
                    >
                        <DemandAnalysisLineChart
                            series={series}
                            options={options}
                        />
                    </Flex>
                ) : (
                    <Flex
                        width="100%"
                        justifyContent="center"
                        alignItems="center"
                        height="274px"
                    >
                        <NoData
                            noDataTitleKey="global.nodata.notavilable"
                            noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                        />
                    </Flex>
                )}
            </div>
        </PulseStyles>
    );
};
